import React, { FunctionComponent, useCallback, useMemo } from 'react';

import dateToString from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { Link } from '@yandex-360/iconostasis/dist/icons/Link';
import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { Empty } from '@yandex-360/orb-components/dist/components/Empty';
import { ScrollBar } from '@yandex-360/orb-components/dist/components/ScrollBar';
import { SplitGroup } from '@yandex-360/orb-components/dist/components/SplitGroup';
import { Text } from '@yandex-360/orb-components/dist/components/Text';
import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { selectFullHistoryArtifactsEnabled } from '../../../../shared/features/flags/selectors';
import { NOTIFICATIONS_MAP } from '../../../../shared/features/notifications/ps-notifications';
import { i18n } from '../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { ArtifactStatus } from '../../../../shared/helpers/transport/cloud-api/types';
import { useActions } from '../../../../shared/hooks';
import { NotificationId } from '../../../call/types';
import { formatFromTo } from '../../helpers/format-date';
import { getArtifactSubtitle } from '../../helpers/get-artifact-subtitle';
import { useIsOneColLayout } from '../../hooks';
import {
    selectIsArtifactsLoading,
    selectSelectedConfArtifacts,
    selectSelectedSession,
    selectSelectedSessionId,
} from '../../selectors';
import { ConferenceHistoryItem } from '../../types';
import { Artifact } from './components/artifact';
import { Header } from './components/header';
import { ArtifactsLoadingSkeleton } from './components/loading-skeleton';
import { RecipientsSelect } from './components/recipients-select';
import { copyToCB, downloadReport, fetchMoreArtifactRecipients } from './slice';
import styles from './styles.css';

export const ArtifactsSidebar: FunctionComponent = () => {
    const isArtifactsLoading = useSelector(selectIsArtifactsLoading);
    const selectedSessionId = useSelector(selectSelectedSessionId);
    const confArtifacts = useSelector(selectSelectedConfArtifacts);
    const selectedSession = useSelector(selectSelectedSession) as ConferenceHistoryItem;
    const actions = useActions({
        copyToCB,
        downloadReport,
        fetchMoreArtifactRecipients,
    });
    const { hasMeetingReport, sessionId, shortUrlId, startedAt } = selectedSession || {};
    const serializedStartedAt = useMemo(() => dateToString(new Date(startedAt), 'YYYY-MM-DD'), [startedAt]);
    const onCopyUrlClick = useCallback(
        (url?: string) => {
            if (!url) return;
            countMetrika(
                'meeting_history',
                'meeting_stat',
                'cloud_record_info',
                'copy_cr_link_button',
                shortUrlId,
                serializedStartedAt
            );
            actions.copyToCB({
                value: url,
                notificationItem: NOTIFICATIONS_MAP[NotificationId.LINK_COPIED_CONF_HISTORY](),
            });
        },
        [shortUrlId, serializedStartedAt, actions]
    );
    const isOneColLayout = useIsOneColLayout();
    const historyFullArtifacts = useSelector(selectFullHistoryArtifactsEnabled);
    const onDownloadReportButtonPress = useCallback(() => {
        countMetrika(
            'meeting_history',
            'meeting_stat',
            'download_participation_report_click_button',
            shortUrlId,
            serializedStartedAt
        );
        actions.downloadReport();
    }, [actions, serializedStartedAt, shortUrlId]);
    if (!selectedSessionId) {
        return (
            <Empty
                className={styles.empty}
                title={
                    <Text typography="body-lg-regular" color="secondary">
                        {i18n('conferences-history', 'select-conference')}
                    </Text>
                }
            />
        );
    }

    return (
        <ScrollBar className={cn(styles.sidebar, { [styles.oneColLayout]: isOneColLayout })}>
            <Header />
            {isArtifactsLoading && <ArtifactsLoadingSkeleton />}
            {!isArtifactsLoading && (
                <div className={styles.artifacts}>
                    {historyFullArtifacts && confArtifacts.cloudRecordings.length > 0 && (
                        <div className={styles.artifactsGroup}>
                            <Text typography="body-lg-medium" className={styles.title}>
                                {i18n('conferences-history', 'cloud-recording')}
                            </Text>
                            {confArtifacts.cloudRecordings.map((artifact) => {
                                if (!artifact.startedAt || !artifact.stoppedAt) return <></>;
                                const formattedDates = formatFromTo(artifact.startedAt, artifact.stoppedAt);
                                const artifactFailed = artifact.status === ArtifactStatus.FAILED;
                                const artifactNotReady = artifact.status === ArtifactStatus.IN_PROGRESS;
                                const subTitle = getArtifactSubtitle(artifact, 'cloudRecordings');
                                return (
                                    <Artifact
                                        key={artifact.id}
                                        title={`${i18n('conferences-history', 'recording')} ${formattedDates}`}
                                        subTitle={subTitle}
                                    >
                                        {!artifactFailed && artifact.amIRecipient && (
                                            <SplitGroup>
                                                <Button
                                                    elementType="a"
                                                    href={artifact.link?.href}
                                                    target="_blank"
                                                    isDisabled={artifactNotReady}
                                                    onPress={() =>
                                                        countMetrika(
                                                            'meeting_history',
                                                            'meeting_stat',
                                                            'cloud_record_info',
                                                            'cr_view_button',
                                                            shortUrlId,
                                                            serializedStartedAt
                                                        )
                                                    }
                                                >
                                                    {i18n('conferences-history', 'watch')}
                                                </Button>
                                                <TooltipStateful
                                                    content={i18n('common', 'copy-link-action')}
                                                    trigger="hover"
                                                    placement="top right"
                                                    className={styles.tooltip}
                                                >
                                                    <Button
                                                        isDisabled={artifactNotReady}
                                                        onPress={() => onCopyUrlClick(artifact.link?.href)}
                                                        icon={<Link variant="outline" size="sm" />}
                                                    />
                                                </TooltipStateful>
                                            </SplitGroup>
                                        )}
                                        {!artifactFailed && !artifact.amIRecipient && (
                                            <RecipientsSelect
                                                placeholder={i18n('conferences-history', 'who-recording')}
                                                artifact={artifact}
                                                onFetchMoreRecipientsPress={() =>
                                                    actions.fetchMoreArtifactRecipients({
                                                        confShortUrlId: shortUrlId,
                                                        sessionId: sessionId,
                                                        offset: artifact.recipients.length,
                                                        artifactType: 'cloudRecordings',
                                                        artifactId: artifact.id,
                                                    })
                                                }
                                                onPress={() =>
                                                    countMetrika(
                                                        'meeting_history',
                                                        'meeting_stat',
                                                        'cloud_record_info',
                                                        'сr_click_button',
                                                        shortUrlId,
                                                        serializedStartedAt
                                                    )
                                                }
                                                onCopyEmailPress={() => {
                                                    countMetrika(
                                                        'meeting_history',
                                                        'meeting_stat',
                                                        'cloud_record_info',
                                                        'сr_copy_mail_button',
                                                        shortUrlId,
                                                        serializedStartedAt
                                                    );
                                                }}
                                            />
                                        )}
                                    </Artifact>
                                );
                            })}
                        </div>
                    )}

                    {historyFullArtifacts && confArtifacts.localRecordings.length > 0 && (
                        <div className={styles.artifactsGroup}>
                            <Text typography="body-lg-medium" className={styles.title}>
                                {i18n('conferences-history', 'local-recording')}
                            </Text>
                            {confArtifacts.localRecordings.map((artifact) => {
                                return (
                                    <Artifact
                                        key={artifact.id}
                                        title={i18n('conferences-history', 'members-recording')}
                                    >
                                        <RecipientsSelect
                                            placeholder={i18n('conferences-history', 'who-recording')}
                                            artifact={artifact}
                                            onFetchMoreRecipientsPress={() =>
                                                actions.fetchMoreArtifactRecipients({
                                                    confShortUrlId: shortUrlId,
                                                    sessionId: sessionId,
                                                    offset: artifact.recipients.length,
                                                    artifactType: 'localRecordings',
                                                    artifactId: artifact.id,
                                                })
                                            }
                                            onPress={() =>
                                                countMetrika(
                                                    'meeting_history',
                                                    'meeting_stat',
                                                    'local_record_info',
                                                    'lr_click_button',
                                                    shortUrlId,
                                                    serializedStartedAt
                                                )
                                            }
                                            onCopyEmailPress={() => {
                                                countMetrika(
                                                    'meeting_history',
                                                    'meeting_stat',
                                                    'local_record_info',
                                                    'lr_copy_mail_button',
                                                    shortUrlId,
                                                    serializedStartedAt
                                                );
                                            }}
                                        />
                                    </Artifact>
                                );
                            })}
                        </div>
                    )}
                    {historyFullArtifacts && confArtifacts.summarizations.length > 0 && (
                        <div className={styles.artifactsGroup}>
                            <Text typography="body-lg-medium" className={styles.title}>
                                {i18n('settings-v2', 'permissions-group-summarization-v2')}
                            </Text>
                            {confArtifacts.summarizations.map((artifact) => {
                                if (!artifact.startedAt || !artifact.stoppedAt) return <></>;
                                const formattedDates = formatFromTo(artifact.startedAt, artifact.stoppedAt);
                                const artifactFailed = artifact.status === ArtifactStatus.FAILED;
                                const artifactNotReady = artifact.status === ArtifactStatus.IN_PROGRESS;
                                const subTitle =
                                    getArtifactSubtitle(artifact, 'summarization') ||
                                    (artifact.amIRecipient && i18n('conferences-history', 'in-your-mail')) ||
                                    '';
                                return (
                                    <Artifact
                                        key={artifact.id}
                                        title={`${i18n('conferences-history', 'summarization')} ${formattedDates}`}
                                        subTitle={subTitle}
                                    >
                                        {!artifactFailed && (
                                            <RecipientsSelect
                                                placeholder={i18n('conferences-history', 'who-receive')}
                                                artifact={artifact}
                                                isDisabled={artifactNotReady}
                                                onFetchMoreRecipientsPress={() =>
                                                    actions.fetchMoreArtifactRecipients({
                                                        artifactId: artifact.id,
                                                        confShortUrlId: shortUrlId,
                                                        sessionId: sessionId,
                                                        offset: artifact.recipients.length,
                                                        artifactType: 'summarizations',
                                                    })
                                                }
                                                onPress={() =>
                                                    countMetrika(
                                                        'meeting_history',
                                                        'meeting_stat',
                                                        'summary_info',
                                                        'summary_click_button',
                                                        shortUrlId,
                                                        serializedStartedAt
                                                    )
                                                }
                                                onCopyEmailPress={() => {
                                                    countMetrika(
                                                        'meeting_history',
                                                        'meeting_stat',
                                                        'summary_info',
                                                        'sum_copy_mail_button',
                                                        shortUrlId,
                                                        serializedStartedAt
                                                    );
                                                }}
                                            />
                                        )}
                                    </Artifact>
                                );
                            })}
                        </div>
                    )}
                    {hasMeetingReport && (
                        <div className={styles.meetingReport}>
                            <Text typography="body-lg-medium" className={styles.title}>
                                {i18n('conferences-history', 'meeting-report')}
                            </Text>
                            <Button onPress={onDownloadReportButtonPress}>
                                {i18n('error-page', 'unsupported-electron-button')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </ScrollBar>
    );
};
